import type { Item } from '@/models';
import type { HealthcheckType } from '@/pages/HealthDashboard/HealthContainer';
import clientUtils from '@/utils/clientUtils';

import { baseAxios, fuelToolsApiAxios } from '../interceptor';

export const FuelToolsApiService = {
  /**
   * Generic method for any POST request
   * @param route The relative route in one of the Fuel Tools API controllers. eg ads/AdTag
   * @param data The data to send in the body of the post request, if any. Can be null.
   * @returns The response from the post request
   */
  async post(route: string, data: object) {
    // console.log('FuelToolsApiService.post', { route, data });
    try {
      const fullRoute = `${route}`;
      const res = await fuelToolsApiAxios.post(fullRoute, data);
      return res.data;
    } catch (err) {
      console.error('err', err);
      throw err;
    }
  },

  /**
   * Generic method for any GET request.
   * @param route The relative route in one of the Fuel Tools API controllers. eg 'ads/AdTag'
   * @param queryParams An object of parameters to add to the query string.  eg. { 'ouId': 'abc' }
   * @returns The result of the GET request.
   */
  async get(route: string, queryParams: object = null) {
    // console.log('FuelToolsApiService.get', { route, queryParams });
    let fullRoute = `${route}`;
    if (queryParams != null) {
      const qs = Object.keys(queryParams)
        .map((key) => `${key}=${queryParams[key]}`)
        .join('&');
      fullRoute += `?${qs}`;
    }
    try {
      const res = await fuelToolsApiAxios.get(fullRoute).catch((err) => {
        return err;
      });
      return res.data;
    } catch (err) {
      console.error('err', err);
      throw err;
    }
  },
};

function showError(err) {
  let errorMessage =
    'Request was not allowed! Please validate the inputs and configurations are correct. Contact support for more details.';
  if (err && err.response && err.response.data) {
    errorMessage = err.response.data;
  }
  if (err && err.data && err.data.Message && !err.data.InnerException) {
    // console.log('!InnerException', err.data.InnerException);
    errorMessage = err.data.Message;
  }
  if (err && err.data && err.data.Message && err.data.InnerException && err.data.InnerException.Message) {
    // console.log('InnerException', err.data.InnerException);
    errorMessage = err.data.InnerException.Message;
  }
  if (err && err.data && !err.data.Message) {
    errorMessage = err.data;
  }
  throw new Error(errorMessage);
}

export const getAnalyticsQuery = async (ouId: string, filters: any): Promise<any> => {
  const url = 'analytics/query';
  try {
    const res = await fuelToolsApiAxios.get(url, {
      params: {
        ouIds: ouId,
        ...filters,
      },
    });
    return res.data;
  } catch (err) {
    console.error('Error fetching analytics query:', err);
    throw err;
  }
};

export const listAppusers = async (ouId: string, page: number = 0, size: number = 100): Promise<any> => {
  const url = 'users';
  try {
    const res = await fuelToolsApiAxios.get(url, {
      params: {
        ouId,
        page,
        size,
      },
    });
    return res.data;
  } catch (err) {
    console.error('Error fetching app users:', err);
    throw err;
  }
};

export const postToFuelToolsApi = async (url: string, data: any): Promise<any> => {
  try {
    const res = await fuelToolsApiAxios.post(url, data);
    return res;
  } catch (err) {
    console.error(`Error posting to ${url}:`, err);
    throw err;
  }
};

export const deleteUser = async (userId: string): Promise<any> => {
  const url = `/users/${userId}`;
  try {
    const res = await fuelToolsApiAxios.delete(url);
    return res;
  } catch (err) {
    console.error(`Error deleting ${url}:`, err);
    throw err;
  }
};

export const getUserById = async (userId: string): Promise<any> => {
  const url = `/users/${userId}`;
  try {
    const res = await fuelToolsApiAxios.get(url);
    return res.data;
  } catch (err: any) {
    console.error(`Error getting user ${url}:`, err);
    throw err;
  }
};

export const getList = async <T>(
  entityType: string,
  queryParams: Record<string, any>,
  additionalQueryString: string | null = null
): Promise<T> => {
  let url = `data/${entityType}/list`;
  const source = baseAxios.CancelToken.source();

  if (queryParams) {
    url += `?${clientUtils.convertToQueryString(queryParams)}`;
  }
  if (additionalQueryString) {
    url += additionalQueryString;
  }

  try {
    const res = await fuelToolsApiAxios.get<T>(url, {
      cancelToken: source.token,
    });
    return res.data;
  } catch (err) {
    console.error('Unable to get list data err', { err });
    if (!baseAxios.isCancel(err)) {
      console.error('not successfully aborted', { err });
    }
    throw err; // better to throw so the caller can handle errors properly
  }
};

export const getEnityById = async <T>(entityType: string, id: string, queryParams: Record<string, any> = null): Promise<T> => {
  let url = `data/${entityType}/${id}`;
  const source = baseAxios.CancelToken.source();

  if (queryParams) {
    url += `?${clientUtils.convertToQueryString(queryParams)}`;
  }
  try {
    const res = await fuelToolsApiAxios.get<T>(url, {
      cancelToken: source.token,
    });
    return res.data;
  } catch (err) {
    console.error('Unable to get entity data err', { err });
    if (!baseAxios.isCancel(err)) {
      console.error('not successfully aborted', { err });
    }
    throw err; // better to throw so the caller can handle errors properly
  }
};

export const upsertEntity = async <T>(entityType: string, payload: any): Promise<T> => {
  const url = `data/${entityType}`;
  const source = baseAxios.CancelToken.source();
  try {
    const res = await fuelToolsApiAxios.post<T>(url, payload, {
      cancelToken: source.token,
    });
    return res.data;
  } catch (err) {
    console.error('Unable to get entity data err', { err });
    if (!baseAxios.isCancel(err)) {
      console.error('not successfully aborted', { err });
    }
    throw err; // better to throw so the caller can handle errors properly
  }
};
export const BatchDelete = async <BatchDeleteResponse>(
  itemIds: string[],
  ouId: string,
  entityType: string
): Promise<BatchDeleteResponse> => {
  const data = {
    ouId,
    ids: itemIds,
  };
  const url = `data/${entityType}/batchdelete`;
  const method = 'post';
  try {
    const res = await fuelToolsApiAxios[method](url, data);
    return res.data;
  } catch (err) {
    console.error('Unable to get delete data err', { err });
    if (!baseAxios.isCancel(err)) {
      console.error('not successfully aborted', { err });
    }
    showError(err);
    return null;
  }
};

export const BatchGet = async <T>(entityType: string, itemIds: string[]): Promise<T[]> => {
  const data = {
    itemIds,
  };
  const url = `data/${entityType}/batchget`;
  const method = 'post';
  try {
    const res = await fuelToolsApiAxios[method](url, itemIds);
    return res.data as T[];
  } catch (err) {
    console.error('Unable to get delete data err', { err });
    if (!baseAxios.isCancel(err)) {
      console.error('not successfully aborted', { err });
    }
    showError(err);
    return null;
  }
};
export const getDefaultTemplates = async (): Promise<any> => {
  const url = `Data/notificationTemplates`;
  const method = 'get';
  try {
    const res = await fuelToolsApiAxios[method](url);
    return res.data;
  } catch (err) {
    console.error('Unable to get delete data err', { err });
    if (!baseAxios.isCancel(err)) {
      console.error('not successfully aborted', { err });
    }
    showError(err);
    return null;
  }
};

// Ou Healthcheck Dashboard services
export const fetchHealthcheckData = async (
  ouId: string,
  ouIds: string[],
  healthCheckType: HealthcheckType,
  queryParams: any = null
): Promise<Item[]> => {
  let url = `healthcheck/${ouId}/${healthCheckType}`;

  // console.log('fetchHealthcheckData', { ouId, ouIds, healthCheckType, queryParams, url });

  if (queryParams) {
    url = `${url}?${clientUtils.convertToQueryString(queryParams)}`;
    if (ouIds && ouIds.length > 0) {
      url += `&ouIds=${ouIds.join('&ouIds=')}`;
    }
  } else if (ouIds && ouIds.length > 0) {
    url = `${url}?ouIds=${ouIds.join('&ouIds=')}`;
  }

  try {
    const source = baseAxios.CancelToken.source();
    const res = await fuelToolsApiAxios.get(url, {
      cancelToken: source.token,
    });
    // console.log('healthCheckType', { healthCheckType, data: res.data });
    return res.data;
  } catch (err) {
    console.error('Unable to fetchHealthcheckData by ouId & ouIds & healthCheckType', { ouId, ouIds, healthCheckType, err });
    if (!baseAxios.isCancel(err)) {
      console.error('not successfully aborted', err);
    }
    showError(err);
    throw err; // better to throw so the caller can handle errors properly
  }
};
